import classnames from 'classnames'
import throttle from 'lodash.throttle'
import * as React from 'react'

import { Icon } from '../../../main/components/icon'
import * as hoverChart from '../../shared/hover-chart'
import TilesStore from '../stores/tilesStore'
import Sparkline from './CanvasSparkline'

interface MapCondensedTileProps {
  ticker: string
  showSparkline: boolean
  hoverOffsetX: number
  hoverOffsetY: number
  colorScale: (d: number) => string
}

function getTileState(ticker: string, prevWidth = 0) {
  var tile = TilesStore.getTile(ticker)
  var group = TilesStore.getTilesGroup(tile.ticker).toLowerCase()

  return {
    ...tile,
    group,
    width: prevWidth,
  }
}

export class MapCondensedTile extends React.Component<React.PropsWithChildren<MapCondensedTileProps>> {
  state = getTileState(this.props.ticker)

  resizeObserver?: ResizeObserver
  containerRef = React.createRef<HTMLAnchorElement>()

  componentDidMount() {
    TilesStore.addChangeListener(this._onChange)

    // Resize sparkline on window resize
    const container = this.containerRef.current
    if (container && this.state.sparkline) {
      this.setState({ width: container.getBoundingClientRect().width }, () => {
        this.resizeObserver = new ResizeObserver(
          throttle(([entry]: ResizeObserverEntry[]) => {
            this.setState({ width: entry.contentRect.width })
          }, 100)
        )
        this.resizeObserver.observe(container)
      })
    }
  }

  componentWillUnmount() {
    TilesStore.removeChangeListener(this._onChange)
    this.resizeObserver?.disconnect()
  }

  _onChange = () => {
    this.setState(getTileState(this.props.ticker, this.state.width))
  }

  render() {
    const { colorScale, children, showSparkline, hoverOffsetX, hoverOffsetY } = this.props
    var { ticker, width, label, last, prevClose, change, high, low, sparkline, sparklineDateChanges } = this.state

    var format = (n: number) => (n >= 0 ? '+' : '') + n.toFixed(2)
    var places = last > 6 ? 2 : 4
    var round = (n: number, plusSign?: string) =>
      (n >= 0 ? plusSign || '' : '') + (+(Math.round(Number(n + 'e+' + places)) + 'e-' + places)).toFixed(places)

    var range = (high - low) * 0.1
    var highlightHigh = last > high - range
    var highlightLow = last < low + range
    const backgroundColor = colorScale(change)
    const changeUSD = last - prevClose

    const chartImage = hoverChart.getHoverChartImg(
      '@' + ticker.toLowerCase(),
      false,
      FinvizSettings.hoverCharts?.futures?.timeframe,
      FinvizSettings.hoverCharts?.futures?.dateRange
    )

    const boxover = `cssbody=[hoverchart] cssheader=[tabchrthdr] body=[${chartImage.img}] offsetx=[${hoverOffsetX}] offsety=[${hoverOffsetY}] delay=[100]`

    if (showSparkline) {
      return (
        <a
          ref={this.containerRef}
          className="w-full overflow-hidden font-sans text-3xs text-white font-medium hover:z-10 hover:rounded hover:text-white hover:outline hover:outline-2 hover:outline-yellow-200"
          href={'/futures_charts.ashx?p=d&t=' + ticker}
          style={{ backgroundColor }}
          data-boxover={boxover}
        >
          {children}
          <div className="w-full whitespace-nowrap p-1">
            <div className="flex w-full items-baseline justify-between overflow-hidden drop-shadow-tile-small">
              <div className="grow truncate text-3xs uppercase leading-none">{label}</div>
              <div className="flex shrink-0 items-center pl-1 tabular-nums leading-none">
                {round(changeUSD, '+')}
                <Icon
                  name="triangle"
                  width={8}
                  className={classnames('ml-0.5 opacity-80', { 'rotate-180': changeUSD < 0 })}
                />
              </div>
            </div>
            <div className="flex items-center justify-between tabular-nums">
              <div className="text-xs drop-shadow-tile-big font-bold xl:text-base xl:leading-tight">{round(last)}</div>
              <div className="-mt-0.5 flex shrink-0 items-center pl-1 leading-none drop-shadow-tile-small">
                {format(change)} <span className="ml-0.5 w-2">%</span>
              </div>
            </div>
          </div>
          <div className="w-full overflow-hidden pb-0.5">
            {showSparkline && (
              <Sparkline
                width={Math.ceil(width)}
                height={42}
                data={sparkline}
                dateChanges={sparklineDateChanges}
                change={change}
                changeDomain={1.3}
                changeOpacity={false}
                shadow={true}
              />
            )}
          </div>
        </a>
      )
    }

    return (
      <a
        className="flex w-full flex-col items-start p-1 font-sans text-3xs text-white font-medium hover:z-10 hover:rounded hover:text-white hover:outline hover:outline-2 hover:outline-yellow-200"
        href={'/futures_charts.ashx?p=d&t=' + ticker}
        style={{ backgroundColor }}
        data-boxover={boxover}
      >
        {children}
        <div className="uppercase leading-none drop-shadow-tile-small xl:text-2xs xl:leading-none">{label}</div>
        <div className="text-xl drop-shadow-tile-big font-bold xl:text-2xl">{round(last)}</div>
        <div className="w-full whitespace-nowrap tabular-nums leading-none xl:mt-0.5">
          <div className="flex items-center justify-between">
            <div
              className={classnames('-mx-0.5 rounded-sm p-0.5', {
                'drop-shadow-tile-small': !highlightHigh,
                'bg-yellow-200': highlightHigh,
              })}
            >
              <span className={classnames('inline-block w-2.5 pr-0.5 font-normal', { 'text-gray-600': highlightHigh })}>
                H
              </span>
              <span className={classnames({ 'text-gray-800': highlightHigh })}>{round(high)}</span>
            </div>
            <div className="flex items-center drop-shadow-tile-small">
              {round(changeUSD, '+')}
              <Icon
                name="triangle"
                width={8}
                className={classnames('ml-0.5 opacity-80', { 'rotate-180': changeUSD < 0 })}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div
              className={classnames('-mx-0.5 rounded-sm p-0.5', {
                'drop-shadow-tile-small': !highlightLow,
                'bg-yellow-200': highlightLow,
              })}
            >
              <span className={classnames('inline-block w-2.5 pr-0.5 font-normal', { 'text-gray-600': highlightLow })}>
                L
              </span>
              <span className={classnames({ 'text-gray-800': highlightLow })}>{round(low)}</span>
            </div>
            <div className="flex shrink-0 items-center drop-shadow-tile-small">
              {format(change)} <span className="ml-0.5 w-2">%</span>
            </div>
          </div>
        </div>
      </a>
    )
  }
}
